import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { Inbox } from 'lucide';
import { NbIcon } from '../../icon/icon';

/**
 * `<nb-empty-state>` — the "nothing here yet" pattern (design.md §5.5).
 *
 * A centred column: an illustration (project your own into the `[nbIllustration]`
 * slot, otherwise a faint 40px `Inbox` glyph), the `title`, projected
 * description text, and an optional action projected via `[nbStateAction]`
 * (typically an `<nb-button>`). The host is a `role="status"` region so the
 * empty message is announced politely when it appears.
 *
 * @example
 * <nb-empty-state title="Ingen oppslag ennå">
 *   Bli den første til å dele noe med nabolaget.
 *   <nb-button nbStateAction variant="pri">Nytt oppslag</nb-button>
 * </nb-empty-state>
 */
@Component({
  selector: 'nb-empty-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  host: {
    class: 'empty',
    role: 'status',
  },
  template: `<div class="ill">
      <ng-content select="[nbIllustration]">
        <nb-icon class="ill-default" [icon]="Inbox" [size]="40" />
      </ng-content>
    </div>
    @if (level() === 3) {
      <h3 class="title">{{ title() }}</h3>
    } @else {
      <h2 class="title">{{ title() }}</h2>
    }
    <p class="desc"><ng-content /></p>
    <div class="action"><ng-content select="[nbStateAction]" /></div>`,
  styleUrl: './empty-state.css',
})
export class NbEmptyState {
  /** The headline of the empty state. */
  readonly title = input.required<string>();
  /** Heading level of the title. Default `2` (a page's next level after its h1). */
  readonly level = input<2 | 3>(2);

  protected readonly Inbox = Inbox;
}
